import React, { useState } from 'react';
import axios from 'axios';
import { X, Download, CheckCircle2, Minimize2, ChevronUp } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import '../styles/exit-popup.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ExitIntentPopup = ({ isOpen, onClose, onSubmit }) => {
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [isSuccess, setIsSuccess] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);

  if (!isOpen) return null;

  const isWorkEmail = (email) => {
    const freeEmailProviders = [
      'gmail.com', 'yahoo.com', 'hotmail.com', 'outlook.com', 
      'aol.com', 'icloud.com', 'mail.com', 'protonmail.com',
      'yandex.com', 'zoho.com', 'gmx.com', 'live.com', 'msn.com'
    ];
    
    const domain = email.split('@')[1]?.toLowerCase();
    return domain && !freeEmailProviders.includes(domain);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    if (!email || !/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(email)) {
      setError('Please enter a valid email address');
      return;
    }
    
    if (!isWorkEmail(email)) {
      setError('Please use your work email address');
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      // Submit to backend to track download and send notification
      await axios.post(`${API}/peo-guide-download`, {
        email: email,
        source: 'exit_popup'
      });
      
      // Call parent onSubmit
      onSubmit(email);
    } catch (error) {
      // Continue with download even if tracking fails
    }
    
    // Wait a moment, then trigger download
    setTimeout(() => {
      // Trigger PDF download
      const pdfUrl = process.env.REACT_APP_PEO_BUYERS_GUIDE_PDF || 'https://customer-assets.emergentagent.com/job_69a9ffbf-3b0d-4791-80d1-6eb6aacb68ee/artifacts/2bri9ad0_The%20Ultimate%20PEO%20Buyer%27s%20Guide.pdf';
      const link = document.createElement('a');
      link.href = pdfUrl;
      link.download = 'PEO-Buyers-Guide.pdf';
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      setIsSubmitting(false);
      setIsSuccess(true);
      
      // Close popup after showing success
      setTimeout(() => {
        onClose();
        setIsSuccess(false);
        setEmail('');
      }, 2000);
    }, 500);
  };

  // Minimized state - show small bar at bottom
  if (isMinimized) {
    return (
      <div className="exit-popup-minimized" onClick={() => setIsMinimized(false)}>
        <Download size={18} />
        <span>Get Free PEO Buyers Guide</span>
        <ChevronUp size={18} />
      </div>
    );
  }

  if (isSuccess) {
    return (
      <div className="exit-popup-overlay" onClick={onClose}>
        <div className="exit-popup-content exit-popup-success" onClick={(e) => e.stopPropagation()}>
          <div className="success-icon">
            <CheckCircle2 size={64} />
          </div>
          <h2 className="success-title">Download Starting...</h2>
          <p className="success-message">
            Your PEO Buyers Guide is downloading. Check your downloads folder!
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="exit-popup-overlay" onClick={onClose}>
      <div className="exit-popup-content" onClick={(e) => e.stopPropagation()}>
        <div className="exit-popup-actions">
          <button 
            className="exit-popup-minimize" 
            onClick={() => setIsMinimized(true)}
            title="Minimize"
          >
            <Minimize2 size={20} />
          </button>
          <button className="exit-popup-close" onClick={onClose}>
            <X size={24} />
          </button>
        </div>
        
        <div className="exit-popup-header">
          <div className="exit-popup-icon">
            <Download size={48} />
          </div>
          <h2 className="exit-popup-title">Wait! Don't Leave Empty-Handed</h2>
          <p className="exit-popup-subtitle">
            Get our <strong>PEO Buyers Guide</strong> and discover what you may be missing.
          </p>
        </div>

        <div className="exit-popup-benefits">
          <div className="exit-popup-benefit">
            ✓ PEO Comparison Worksheet
          </div>
          <div className="exit-popup-benefit">
            ✓ Identify Potential Savings
          </div>
        </div>

        <form onSubmit={handleSubmit} className="exit-popup-form">
          <Input
            type="email"
            name="email"
            placeholder="Enter your work email"
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              setError('');
            }}
            autoComplete="email"
            className="exit-popup-input"
            required
          />
          {error && <div className="exit-popup-error">{error}</div>}
          <Button type="submit" className="exit-popup-button" disabled={isSubmitting}>
            {isSubmitting ? 'Sending...' : 'Download PEO Buyers Guide'}
          </Button>
        </form>

        <p className="exit-popup-privacy">
          No spam. Unsubscribe anytime. We respect your privacy.
        </p>
      </div>
    </div>
  );
};

export default ExitIntentPopup;
